import { formatCurrency, formatInvoiceDate } from "@/lib/invoiceStatus";
import { PROCESSING_STATUSES, REVIEW_STATUSES } from "@/lib/statusEnums";

/**
 * Turns a filter object into the list of chips describing what is currently
 * applied.
 *
 * Every list page in the old app rendered this row — `renderFilterBadge` on
 * Invoices, My Invoices, Failed, In Query, Approval Requests and Logs. The new
 * app replaced all six with a `(3)` count on the Filter button, so the only way
 * to find out *which* three were narrowing the table was to reopen the panel.
 * The count stays; this restores the readout, and each chip can now also clear
 * the one filter it names, which the old badges could not do.
 *
 * Each descriptor is `{ key, label, value, patch }`, where `patch` is the
 * partial to merge into the filter object to drop that one filter.
 */

function statusLabel(list, id) {
  return list.find((status) => status.id === Number(id))?.label ?? `#${id}`;
}

function userLabel(users, id) {
  const user = (users ?? []).find((candidate) => String(candidate.id) === String(id));
  return user?.name ?? `User ${id}`;
}

/** Date filters are stored as `YYYY-MM-DD`, so they read like the table's dates. */
function dateLabel(value) {
  return formatInvoiceDate(value);
}

/**
 * One chip per selected entry of a multi-select filter. Removing one rebuilds
 * the array without it rather than clearing the whole dimension.
 */
function describeListFilter(filters, key, label, toLabel) {
  return (filters[key] ?? []).filter(isSet).map((id) => ({
    key: `${key}-${id}`,
    label,
    value: toLabel(id),
    patch: { [key]: (filters[key] ?? []).filter((item) => item !== id) },
  }));
}

export function describeInvoiceFilters(filters, { users = [], currencySymbol = "£" } = {}) {
  if (!filters) return [];

  return [
    ...describeListFilter(filters, "reviewStatusIds", "Review status", (id) =>
      statusLabel(REVIEW_STATUSES, id),
    ),
    ...describeListFilter(filters, "processingStatusIds", "Processing status", (id) =>
      statusLabel(PROCESSING_STATUSES, id),
    ),
    ...describeRangeFilters(filters, { currencySymbol }),
    ...describeUserFilters(filters, users),
  ];
}

/** Whether a filter slot actually holds something. */
function isSet(value) {
  return value !== "" && value != null;
}

/**
 * Shared by the invoice queues and the approval requests queue.
 *
 * Emptiness is decided on the *raw* filter value, never on the formatted one:
 * the formatters deliberately return a placeholder ("—") for a missing value,
 * so testing their output made every date chip pass the check and render as a
 * permanent, unclearable "Created after —".
 */
function describeRangeFilters(filters, { currencySymbol = "£" } = {}) {
  const chips = [];

  const add = (key, label, format) => {
    if (!isSet(filters[key])) return;
    chips.push({ key, label, value: format(filters[key]), patch: { [key]: "" } });
  };

  add("createdOnAfter", "Created after", dateLabel);
  add("createdOnBefore", "Created before", dateLabel);
  add("updatedOnAfter", "Updated after", dateLabel);
  add("updatedOnBefore", "Updated before", dateLabel);
  add("invoiceDateAfter", "Invoice date after", dateLabel);
  add("invoiceDateBefore", "Invoice date before", dateLabel);
  add("totalMin", "Min total", (value) => formatCurrency(value, currencySymbol));
  add("totalMax", "Max total", (value) => formatCurrency(value, currencySymbol));

  return chips;
}

const USER_FILTERS = [
  ["assignedToId", "Assigned to"],
  ["createdById", "Created by"],
  ["updatedById", "Updated by"],
];

function describeUserFilters(filters, users) {
  return USER_FILTERS.filter(([key]) => isSet(filters[key])).map(([key, label]) => ({
    key,
    label,
    value: userLabel(users, filters[key]),
    patch: { [key]: "" },
  }));
}

/**
 * @param {object} options
 * @param {Array}  [options.reviewTypes] `/invoices/review-types`, so a selected
 *   type reads as its name rather than its id.
 */
export function describeApprovalRequestFilters(
  filters,
  { users = [], reviewTypes = [], currencySymbol = "£" } = {},
) {
  if (!filters) return [];

  const reviewTypeLabel = (id) =>
    reviewTypes.find((type) => String(type.id) === String(id))?.name ?? `Type ${id}`;

  return [
    ...describeListFilter(filters, "reviewStatusIds", "Review status", (id) =>
      statusLabel(REVIEW_STATUSES, id),
    ),
    ...describeListFilter(filters, "processingStatusIds", "Processing status", (id) =>
      statusLabel(PROCESSING_STATUSES, id),
    ),
    ...describeListFilter(filters, "reviewTypeIds", "Review type", reviewTypeLabel),
    ...describeListFilter(filters, "failureTypes", "Failure type", (value) => value),
    ...describeRangeFilters(filters, { currencySymbol }),
    ...describeUserFilters(filters, users),
  ];
}

/** Invoice creation logs. Same chip contract as the two invoice queues. */
export function describeLogFilters(filters, { users = [] } = {}) {
  if (!filters) return [];

  const chips = [];
  const add = (key, label, format = (value) => value) => {
    if (!isSet(filters[key])) return;
    chips.push({ key, label, value: format(filters[key]), patch: { [key]: "" } });
  };

  add("status", "Status");
  add("createdById", "Created by", (id) => userLabel(users, id));
  add("createdOnAfter", "Created after", dateLabel);
  add("createdOnBefore", "Created before", dateLabel);
  add("invoiceGuid", "Invoice");

  return chips;
}
